import { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../components/base/Button";
import SEO from "../../../components/base/SEO";
import Footer from "../../../components/feature/Footer";
import Header from "../../../components/feature/Header";

const moduleColors = {
  teal: {
    chip: "bg-teal-100 text-teal-700",
    border: "border-teal-200",
    ring: "ring-teal-500",
    panel: "from-teal-500 to-cyan-500",
    soft: "bg-teal-50",
    text: "text-teal-700",
    button: "border-teal-300 hover:border-teal-500 hover:bg-teal-50",
  },
  amber: {
    chip: "bg-amber-100 text-amber-700",
    border: "border-amber-200",
    ring: "ring-amber-500",
    panel: "from-amber-500 to-orange-500",
    soft: "bg-amber-50",
    text: "text-amber-700",
    button: "border-amber-300 hover:border-amber-500 hover:bg-amber-50",
  },
  sky: {
    chip: "bg-sky-100 text-sky-700",
    border: "border-sky-200",
    ring: "ring-sky-500",
    panel: "from-sky-500 to-blue-500",
    soft: "bg-sky-50",
    text: "text-sky-700",
    button: "border-sky-300 hover:border-sky-500 hover:bg-sky-50",
  },
  rose: {
    chip: "bg-rose-100 text-rose-700",
    border: "border-rose-200",
    ring: "ring-rose-500",
    panel: "from-rose-500 to-red-500",
    soft: "bg-rose-50",
    text: "text-rose-700",
    button: "border-rose-300 hover:border-rose-500 hover:bg-rose-50",
  },
  violet: {
    chip: "bg-violet-100 text-violet-700",
    border: "border-violet-200",
    ring: "ring-violet-500",
    panel: "from-violet-500 to-fuchsia-500",
    soft: "bg-violet-50",
    text: "text-violet-700",
    button: "border-violet-300 hover:border-violet-500 hover:bg-violet-50",
  },
  slate: {
    chip: "bg-slate-100 text-slate-700",
    border: "border-slate-200",
    ring: "ring-slate-500",
    panel: "from-slate-600 to-slate-800",
    soft: "bg-slate-50",
    text: "text-slate-700",
    button: "border-slate-300 hover:border-slate-500 hover:bg-slate-50",
  },
} as const;

type ThirdPartyModule = {
  id: string;
  label: string;
  short: string;
  title: string;
  description: string;
  bullets: string[];
  outcomes: string[];
  icon: string;
  image: string;
  accent: keyof typeof moduleColors;
  tag: string;
  fit: string;
};

const thirdPartyModules: readonly ThirdPartyModule[] = [
  {
    id: "client-management",
    label: "貨主與客戶管理",
    short: "每個客戶可有獨立 SOP、庫存規則與報表視角",
    title: "先把不同貨主拆清楚，3PL 作業才不會混在一起",
    description:
      "3PL 最核心的不是單一倉務流程，而是同一個倉庫內同時服務多個客戶。AUTOCHAIN 讓每個貨主擁有獨立的商品主檔、作業規則、權限與查詢視角。",
    bullets: [
      "按貨主建立獨立 SKU、條碼、包裝要求與出貨規則",
      "支援不同客戶使用不同文件格式、標籤與作業指示",
      "客戶、倉務與管理層可按權限查看各自需要的資料",
    ],
    outcomes: ["多客戶管理更清晰", "減少貨主資料混淆", "服務規則更易標準化"],
    icon: "ri-building-line",
    image: "/dashboard-autochain.png",
    accent: "teal",
    tag: "Step 1",
    fit: "適合需要同時服務多個貨主、不同品牌與不同出貨規則的 3PL 團隊。",
  },
  {
    id: "inbound-storage",
    label: "入庫、上架與貨位控制",
    short: "由 ASN、驗收、上架到貨位分配放在同一流程內",
    title: "把收貨與上架做成可追溯流程，而不是靠現場經驗執行",
    description:
      "當不同客戶的貨品同時入倉，系統需要先分清來貨身份、數量與貨位安排。AUTOCHAIN 讓 ASN、收貨、驗收、上架與貨位控制可按貨主規則執行。",
    bullets: [
      "支援 ASN、入庫驗收、批次與貨位記錄",
      "按倉位容量、貨主要求與作業優先次序提供上架建議",
      "同時保留貨物來源、批次與操作紀錄，方便日後追查",
    ],
    outcomes: ["入庫流程更穩定", "貨位使用更有秩序", "查貨來源更容易"],
    icon: "ri-inbox-archive-line",
    image: "/inventory-autochain.png",
    accent: "amber",
    tag: "Step 2",
    fit: "適合每日有大量入庫任務、需要兼顧速度與追溯能力的第三方物流倉庫。",
  },
  {
    id: "inventory-operations",
    label: "存倉、盤點與出庫作業",
    short: "存貨、盤點、波次與揀貨任務統一管理",
    title: "由存倉到出庫，讓前線按任務做事，不再靠口頭交接",
    description:
      "AUTOCHAIN 將日常存倉、盤點、補貨、波次、揀貨與包裝安排統一到同一工作台，讓倉務主管更容易控節奏，前線更容易跟流程執行。",
    bullets: [
      "支援 cycle count、全倉盤點與指定貨主盤點",
      "可按波次、客戶、路線或服務時效分派出庫任務",
      "揀貨、覆核、包裝與出庫狀態可即時回報與追蹤",
    ],
    outcomes: ["日常作業更可控", "盤點與出庫更一致", "現場交接更少出錯"],
    icon: "ri-stack-line",
    image: "/sorting-autochain.png",
    accent: "sky",
    tag: "Step 3",
    fit: "適合 SKU 多、作業頻密、需要穩定 SOP 的中大型 3PL 倉務團隊。",
  },
  {
    id: "vas-billing",
    label: "增值服務與計費",
    short: "包裝、貼標、組裝、特殊處理與收費規則可分開管理",
    title: "3PL 真正難的，是把作業與計費同時管理清楚",
    description:
      "除了基本入庫與出庫，3PL 常見的貼標、換盒、組裝、分貨、拍照或特別包裝，都需要獨立記錄並準確計費。AUTOCHAIN 將增值服務與計費規則連在一起處理。",
    bullets: [
      "按貨主定義入庫費、出庫費、倉租、揀貨費與增值服務費",
      "服務完成後自動累積可計費項目，減少月底人手對數",
      "支援按件、按單、按重量、按體積或按操作次數計費",
    ],
    outcomes: ["計費更透明", "對帳爭議更少", "更容易擴展"],
    icon: "ri-calculator-line",
    image: "/87d413c0ac27cea2ec52acf41d4138ee.jpg",
    accent: "rose",
    tag: "Step 4",
    fit: "適合需要提供增值服務、並希望把服務收入與倉務作業連動的 3PL 公司。",
  },
  {
    id: "portal-reporting",
    label: "客戶查詢與營運報表",
    short: "貨主查庫存、查訂單、看 SLA，不用再逐單回覆",
    title: "把查詢壓力由人工回覆，轉成可共享的營運透明度",
    description:
      "當貨主數量增加，客服與倉務主管最怕不停回覆『貨到未』『出咗未』『仲有幾多存貨』。AUTOCHAIN 讓客戶查詢、庫存報表與 SLA 追蹤更有系統。",
    bullets: [
      "按貨主輸出庫存、訂單、出入庫與異常件報表",
      "管理層可追蹤作業表現、人手負荷與服務時效",
      "客戶查詢資料更集中，減少逐單翻查記錄",
    ],
    outcomes: ["查詢速度更快", "服務透明度更高", "管理決策更有依據"],
    icon: "ri-bar-chart-grouped-line",
    image: "/dashboard.png",
    accent: "violet",
    tag: "Step 5",
    fit: "適合已開始承受大量查詢、SLA 壓力與對帳要求的成熟 3PL 營運團隊。",
  },
  {
    id: "automation-integrations",
    label: "自動化設備與系統整合",
    short: "AGV、磅度、TMS 與外部系統可逐步串接",
    title: "當作業量再上去，系統需要接得上設備與外部流程",
    description:
      "3PL 的系統不應只停留在基本倉務。AUTOCHAIN 支援與 AGV、電子標籤、智能磅度、TMS 與其他外部系統整合，讓流程可持續擴展。",
    bullets: [
      "可對接 AGV、電子標籤分貨與自動量度設備",
      "支援與 TMS、快遞 API 或客戶系統交換資料",
      "先建立標準流程，再逐步擴展自動化投資",
    ],
    outcomes: ["擴展路線更清楚", "設備投資更有根據", "倉到運輸流程更順"],
    icon: "ri-robot-line",
    image: "/83742af0759d08dc52845fefa85ef52d.jpg",
    accent: "slate",
    tag: "Step 6",
    fit: "適合正規劃自動化升級，或需要把倉務與運輸串成同一方案的 3PL 企業。",
  },
] as const;

const flowSteps = [
  {
    step: "01",
    title: "貨主設定",
    eyebrow: "每個客戶有獨立規則",
    description:
      "先定義貨主、商品主檔、文件格式、出貨要求與計費模式，確保後續作業不會在同一倉內混淆。",
  },
  {
    step: "02",
    title: "收貨入庫",
    eyebrow: "由 ASN 到驗收上架",
    description:
      "來貨進倉後，按 ASN、批次、貨位與驗收規則入庫，讓每一批貨物有清楚來源與位置。",
  },
  {
    step: "03",
    title: "存倉管理",
    eyebrow: "貨位、補貨與盤點同步控制",
    description:
      "系統持續管理貨位、補貨與盤點節奏，避免貨在倉內找不到、數字對不上或高峰期補貨混亂。",
  },
  {
    step: "04",
    title: "出庫作業",
    eyebrow: "按波次與客戶要求派任務",
    description:
      "按貨主、時效、路線或訂單類型建立波次與揀貨任務，讓前線可跟同一套 SOP 執行出庫。",
  },
  {
    step: "05",
    title: "增值服務",
    eyebrow: "特殊作業同步記錄",
    description:
      "貼標、換盒、組裝、分貨或特殊包裝等增值服務可獨立追蹤，避免做了工但沒有完整記錄。",
  },
  {
    step: "06",
    title: "客戶通知",
    eyebrow: "大量 email trigger 自動發送",
    description:
      "系統可按不同事件自動發送 email 給貨主或客戶，例如收貨完成、收到壞貨、異常件發現或指定作業完成，減少人手逐封通知。",
  },
  {
    step: "07",
    title: "計費對帳",
    eyebrow: "作業完成後自動累積費項",
    description:
      "由倉租、入出庫、揀貨到增值服務，自動累積可計費項目，讓月底對帳與客戶溝通更清楚。",
  },
] as const;

const proofStats = [
  { value: "多貨主", label: "同倉獨立管理" },
  { value: "自動", label: "計費與對帳累積" },
  { value: "SOP", label: "作業流程標準化" },
  { value: "可擴展", label: "系統串接" },
];

const fitScenarios = [
  {
    title: "電商 3PL 倉",
    text: "同時服務多個品牌、出貨要求不同，最怕作業和客戶資料混在一起。",
    icon: "ri-store-2-line",
  },
  {
    title: "B2B / 分銷倉",
    text: "需要處理大批量入出庫、指定文件格式與不同客戶的配貨邏輯。",
    icon: "ri-building-4-line",
  },
  {
    title: "增值服務導向",
    text: "貼標、組裝、換盒、特殊包裝等服務多，必須記錄得清楚又計費準確。",
    icon: "ri-price-tag-3-line",
  },
  {
    title: "計劃自動化升級",
    text: "正考慮接 AGV、磅度、電子標籤或 TMS，希望系統底盤先搭好。",
    icon: "ri-cpu-line",
  },
] as const;

const interfaces = [
  {
    title: "貨主管理面板",
    image: "/3pl-client-management.png",
    description:
      "按客戶查看庫存、待處理作業、異常件與服務表現，減少翻查與交接時間。",
  },
  {
    title: "貨位與庫存控制",
    image: "/3pl-location-inventory.png",
    description:
      "按貨主、貨位、批次與作業狀態管理倉存，讓日常盤點與查貨更有秩序。",
  },
  {
    title: "作業與計費工作台",
    image: "/3pl-operations-billing.png",
    description: "將任務分派、揀貨包裝、增值服務與可計費項目集中到同一流程內。",
  },
] as const;

const pricingItems = [
  "倉租",
  "入庫費",
  "出庫費",
  "揀貨費",
  "包裝費",
  "增值服務費",
] as const;

const enablementItems = [
  "AGV / 穿梭車",
  "電子標籤分貨",
  "智能磅度",
  "TMS 串接",
  "快遞 API",
  "客戶系統交換",
] as const;

export default function ThirdPartyLogisticsWMS() {
  const [showDemo, setShowDemo] = useState(false);
  const [activeModule, setActiveModule] = useState(0);
  const [activeFlow, setActiveFlow] = useState(0);
  const { t } = useTranslation();

  const currentModule = thirdPartyModules[activeModule];
  const currentFlow = flowSteps[activeFlow];
  const color = moduleColors[currentModule.accent];
  const topFlowSteps = flowSteps.slice(0, 4);
  const bottomFlowSteps = flowSteps.slice(4);

  const openWhatsApp = (zhMessage: string, enMessage: string) => {
    const message = encodeURIComponent(
      t("nav.language") === "中" ? zhMessage : enMessage,
    );

    window.open(`https://wa.me/85294252903?text=${message}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="第三方物流 (3PL) WMS 系統供應商 (Vendor) | 香港 3PL 倉儲管理 | AUTOCHAIN"
        description="AUTOCHAIN 第三方物流 WMS 以多客戶、多作業規則與自動計費為核心，支援貨主管理、入庫上架、出庫作業、增值服務、對帳報表與自動化整合。"
        keywords="第三方物流, 3PL WMS, 香港3PL, 3PL系統, WMS Vendor, 多客戶倉庫管理, 自動計費, 增值服務, TPLSP, 倉儲管理系統"
        ogImage="https://autochain.com.hk/wms-og-image.png"
        canonicalUrl="/wms/3pl"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "AUTOCHAIN 第三方物流 WMS 系統",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web Browser",
            description:
              "專為 3PL 第三方物流商設計的 WMS 倉庫管理系統，支援多客戶管理、增值服務、計費對帳與自動化整合。",
            url: "https://autochain.com.hk/wms/3pl",
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.9",
              ratingCount: "12",
            },
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "HKD",
              description: "免費演示",
            },
            provider: {
              "@type": "Organization",
              name: "AUTOCHAIN TECHNOLOGY",
            },
            featureList: thirdPartyModules.map((module) => module.label),
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首頁",
                item: "https://autochain.com.hk/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "WMS 倉庫管理系統",
                item: "https://autochain.com.hk/wms/3pl",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "第三方物流 WMS",
                item: "https://autochain.com.hk/wms/3pl",
              },
            ],
          }),
        }}
      />

      <Header />

      <section className="relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.22),_transparent_30%),radial-gradient(circle_at_82%_18%,_rgba(20,184,166,0.2),_transparent_24%),linear-gradient(135deg,_rgba(15,23,42,0.96),_rgba(15,23,42,0.84))]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 lg:pt-36 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-[1.12fr_0.88fr] gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-slate-200">
                <i className="ri-building-4-line text-sky-300"></i>
                專為香港第三方物流營運而設
              </div>
              <h1 className="mt-6 text-4xl md:text-6xl font-bold leading-tight">
                第三方物流 WMS，
                <span className="block text-sky-300">
                  將倉務作業、客戶要求與計費流程整合起來
                </span>
              </h1>
              <p className="mt-6 max-w-3xl text-lg md:text-xl leading-8 text-slate-200">
                AUTOCHAIN 以 3PL 的實際營運模式為核心，將貨主管理、入庫上架、
                出庫作業、增值服務、計費對帳與客戶查詢整合到同一套系統內，
                協助你用更清晰的方式擴展第三方物流業務。
              </p>

              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
                {proofStats.map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 backdrop-blur"
                  >
                    <div className="text-2xl font-bold text-white">
                      {stat.value}
                    </div>
                    <div className="mt-1 text-sm text-slate-300">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button
                  variant="primary"
                  size="lg"
                  className="text-lg px-8 py-4"
                  onClick={() =>
                    openWhatsApp(
                      "您好！我想了解 AUTOCHAIN 第三方物流 WMS 的完整功能介紹，希望預約演示。",
                      "Hello! I would like to learn more about AUTOCHAIN's 3PL WMS and schedule a demo.",
                    )
                  }
                >
                  預約免費演示
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="text-lg px-8 py-4 bg-white/5 border-white text-white hover:bg-white hover:text-slate-950"
                  onClick={() =>
                    openWhatsApp(
                      "您好！我想索取 AUTOCHAIN 第三方物流 WMS 產品手冊與方案資料。",
                      "Hello! I would like to receive AUTOCHAIN's 3PL WMS brochure and solution details.",
                    )
                  }
                >
                  索取產品手冊
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -left-8 top-10 hidden lg:block h-36 w-36 rounded-full bg-sky-400/20 blur-3xl" />
              <div className="relative rounded-[32px] border border-white/10 bg-white p-4 shadow-2xl shadow-black/30">
                <div className="rounded-[24px] bg-slate-100 p-3">
                  <div className="relative h-[320px] overflow-hidden rounded-[18px] border border-slate-200">
                    <img
                      src="/3pl-hero-autochain-photo.png"
                      alt="AUTOCHAIN 3PL WMS warehouse operations"
                      className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,_rgba(15,23,42,0.12),_rgba(15,23,42,0.18)_35%,_rgba(15,23,42,0.72))]" />

                    <div className="absolute left-5 top-5 rounded-2xl bg-white/92 px-4 py-3 shadow-lg backdrop-blur">
                      <img
                        src="/logo.png"
                        alt="AUTOCHAIN"
                        className="h-7 w-auto"
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
                  <div className="rounded-2xl bg-slate-950 px-5 py-4 text-white">
                    <div className="text-sm text-slate-300">3PL 核心挑戰</div>
                    <div className="mt-2 text-lg font-semibold">
                      同倉管理多貨主、多 SOP 與多收費模式
                    </div>
                  </div>
                  <div className="rounded-2xl bg-sky-50 px-5 py-4">
                    <div className="text-sm text-slate-500">營運目標</div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      用同一套系統撐起可擴展的 3PL 服務能力
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm">
              核心功能總覽
            </span>
            <h2 className="mt-5 text-3xl md:text-4xl font-bold text-slate-900">
              由 3PL 核心模組出發，
              <span className="text-sky-700">建立多客戶營運能力</span>
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-8">
              由貨主管理、入庫上架、存倉出庫、增值服務到計費對帳，
              每個模組都圍繞 3PL
              最常見的營運壓力，協助你把服務做得更清楚、更穩定。
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-8 items-start">
            <div className="space-y-4">
              {thirdPartyModules.map((module, index) => {
                const moduleColor = moduleColors[module.accent];
                const isActive = index === activeModule;

                return (
                  <button
                    key={module.id}
                    type="button"
                    onClick={() => setActiveModule(index)}
                    className={`w-full rounded-3xl border bg-white p-5 text-left transition-all duration-200 ${
                      isActive
                        ? `${moduleColor.border} ring-2 ${moduleColor.ring} shadow-lg`
                        : `border-slate-200 ${moduleColor.button}`
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-2xl text-xl ${
                          isActive
                            ? moduleColor.chip
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        <i className={module.icon}></i>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-4">
                          <div className="text-lg font-semibold text-slate-900">
                            {module.label}
                          </div>
                          <span
                            className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                              isActive
                                ? moduleColor.chip
                                : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {module.tag}
                          </span>
                        </div>
                        <p className="mt-2 text-sm leading-6 text-slate-600">
                          {module.short}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-xl shadow-slate-200/60">
              <div className={`bg-gradient-to-r ${color.panel} p-8 text-white`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full bg-white/15 px-4 py-2 text-sm font-medium backdrop-blur">
                    {currentModule.tag}
                  </span>
                  <span className="rounded-full bg-white/10 px-4 py-2 text-sm backdrop-blur">
                    {currentModule.label}
                  </span>
                </div>
                <h3 className="mt-5 text-3xl font-bold leading-tight">
                  {currentModule.title}
                </h3>
                <p className="mt-4 max-w-3xl text-lg leading-8 text-white/90">
                  {currentModule.description}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-0">
                <div className="border-b border-slate-200 p-8 lg:border-b-0 lg:border-r">
                  <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                    這個模組會做什麼
                  </h4>
                  <div className="mt-5 space-y-4">
                    {currentModule.bullets.map((bullet) => (
                      <div
                        key={bullet}
                        className={`flex items-start gap-3 rounded-2xl ${color.soft} p-4`}
                      >
                        <i
                          className={`ri-check-double-line mt-1 text-lg ${color.text}`}
                        ></i>
                        <p className="leading-7 text-slate-700">{bullet}</p>
                      </div>
                    ))}
                  </div>

                  <h4 className="mt-8 text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                    預期成果
                  </h4>
                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {currentModule.outcomes.map((outcome) => (
                      <div
                        key={outcome}
                        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-center text-sm font-semibold text-slate-800"
                      >
                        {outcome}
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                    <Button
                      variant="primary"
                      onClick={() =>
                        openWhatsApp(
                          `您好！我想重點了解 AUTOCHAIN 第三方物流 WMS 的「${currentModule.label}」模組。`,
                          `Hello! I would like to learn more about the "${currentModule.label}" module in AUTOCHAIN's 3PL WMS.`,
                        )
                      }
                    >
                      了解這個模組
                    </Button>
                    <Button
                      variant="outline"
                      className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                      onClick={() => setShowDemo(true)}
                    >
                      預約演示
                    </Button>
                  </div>
                </div>

                <div className="p-8">
                  <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-4">
                    <img
                      src={currentModule.image}
                      alt={currentModule.label}
                      className="w-full rounded-[20px] object-cover shadow-lg"
                    />
                  </div>
                  <div className="mt-5 rounded-3xl border border-slate-200 bg-white p-5">
                    <div className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                      適合這類 3PL 團隊
                    </div>
                    <p className="mt-3 leading-7 text-slate-700">
                      {currentModule.fit}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-[0.85fr_1.15fr]">
            <div>
              <span className="inline-flex rounded-full bg-sky-100 px-4 py-2 text-sm font-medium text-sky-700">
                營運流程
              </span>
              <h2 className="mt-5 text-3xl md:text-4xl font-bold leading-tight text-slate-900">
                由貨主設定到客戶通知與計費對帳，
                <span className="block text-slate-600">
                  建立完整 3PL 作業鏈
                </span>
              </h2>
              <p className="mt-4 text-lg leading-8 text-slate-600">
                3PL
                的難度在於同時管理倉務服務、客戶要求與收費邏輯。把流程拆清楚，營運才有機會穩定擴展。
              </p>
            </div>

            <div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:hidden">
                {flowSteps.map((flow, index) => {
                  const isActive = index === activeFlow;

                  return (
                    <button
                      key={flow.step}
                      type="button"
                      onClick={() => setActiveFlow(index)}
                      className={`rounded-3xl border px-4 py-4 text-left transition-all ${
                        isActive
                          ? "border-slate-900 bg-slate-900 text-white shadow-lg"
                          : "border-slate-200 bg-white hover:border-slate-400"
                      }`}
                    >
                      <div className="text-sm font-semibold">{flow.step}</div>
                      <div className="mt-2 text-base font-semibold">
                        {flow.title}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="relative hidden lg:block py-2">
                <div className="absolute left-10 right-10 top-1/2 h-px -translate-y-1/2 bg-slate-200"></div>

                <div className="grid grid-cols-4 gap-4">
                  {topFlowSteps.map((flow) => {
                    const index = flowSteps.findIndex(
                      (item) => item.step === flow.step,
                    );
                    const isActive = index === activeFlow;

                    return (
                      <div key={flow.step} className="relative pb-9">
                        <button
                          type="button"
                          onClick={() => setActiveFlow(index)}
                          className={`w-full rounded-[28px] border px-5 py-5 text-left transition-all ${
                            isActive
                              ? "border-slate-900 bg-slate-900 text-white shadow-lg"
                              : "border-slate-200 bg-white hover:border-slate-400"
                          }`}
                        >
                          <div className="text-sm font-semibold">
                            {flow.step}
                          </div>
                          <div className="mt-3 text-xl font-semibold">
                            {flow.title}
                          </div>
                        </button>
                        <div className="absolute bottom-4 left-1/2 h-5 w-px -translate-x-1/2 bg-slate-200"></div>
                        <div
                          className={`absolute bottom-0 left-1/2 h-4 w-4 -translate-x-1/2 rounded-full border-4 ${
                            isActive
                              ? "border-slate-900 bg-white"
                              : "border-slate-300 bg-white"
                          }`}
                        ></div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 grid grid-cols-3 gap-4 px-16 xl:px-20">
                  {bottomFlowSteps.map((flow) => {
                    const index = flowSteps.findIndex(
                      (item) => item.step === flow.step,
                    );
                    const isActive = index === activeFlow;

                    return (
                      <div key={flow.step} className="relative pt-9">
                        <div
                          className={`absolute left-1/2 top-0 h-4 w-4 -translate-x-1/2 rounded-full border-4 ${
                            isActive
                              ? "border-slate-900 bg-white"
                              : "border-slate-300 bg-white"
                          }`}
                        ></div>
                        <div className="absolute left-1/2 top-4 h-5 w-px -translate-x-1/2 bg-slate-200"></div>
                        <button
                          type="button"
                          onClick={() => setActiveFlow(index)}
                          className={`w-full rounded-[28px] border px-5 py-5 text-left transition-all ${
                            isActive
                              ? "border-slate-900 bg-slate-900 text-white shadow-lg"
                              : "border-slate-200 bg-white hover:border-slate-400"
                          }`}
                        >
                          <div className="text-sm font-semibold">
                            {flow.step}
                          </div>
                          <div className="mt-3 text-xl font-semibold">
                            {flow.title}
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-6 rounded-[32px] border border-slate-200 bg-slate-50 p-8">
                <div className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {currentFlow.eyebrow}
                </div>
                <h3 className="mt-3 text-3xl font-bold text-slate-900">
                  {currentFlow.step}. {currentFlow.title}
                </h3>
                <p className="mt-4 text-lg leading-8 text-slate-700">
                  {currentFlow.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200">
              適用場景
            </span>
            <h2 className="mt-5 text-3xl md:text-4xl font-bold text-white">
              適合正在建立標準化服務能力的 3PL 團隊，
              <span className="text-sky-300">
                尤其是已開始承受多貨主管理壓力的公司
              </span>
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            {fitScenarios.map((scenario) => (
              <div
                key={scenario.title}
                className="rounded-[28px] border border-white/10 bg-white/5 p-7 text-white"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-2xl text-sky-300">
                  <i className={scenario.icon}></i>
                </div>
                <h3 className="mt-5 text-xl font-semibold">{scenario.title}</h3>
                <p className="mt-3 leading-7 text-slate-300">{scenario.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <span className="inline-flex rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700">
                介面預覽
              </span>
              <h2 className="mt-5 text-3xl md:text-4xl font-bold text-slate-900">
                用清楚的工作台設計，
                <span className="text-sky-700">支撐多貨主與多任務管理</span>
              </h2>
            </div>
            <p className="max-w-2xl text-lg leading-8 text-slate-600">
              由貨主管理、庫存貨位到任務與計費工作台，系統以 3PL
              日常營運節奏為主，不用再把查詢、作業與對帳拆成多套工具處理。
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {interfaces.map((item) => (
              <div
                key={item.title}
                className="rounded-[30px] border border-slate-200 bg-slate-50 p-5 shadow-sm"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full rounded-[22px] border border-slate-200 bg-white object-cover shadow-lg"
                />
                <h3 className="mt-5 text-xl font-semibold text-slate-900">
                  {item.title}
                </h3>
                <p className="mt-3 leading-7 text-slate-600">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 xl:grid-cols-[1.1fr_0.9fr]">
            <div className="space-y-8">
              <div className="rounded-[34px] bg-white p-8 shadow-xl shadow-slate-200/60">
                <div className="flex items-center gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-100 text-2xl text-sky-700">
                    <i className="ri-file-list-3-line"></i>
                  </div>
                  <div>
                    <div className="text-xl font-semibold text-slate-900">
                      常見 3PL 計費項目
                    </div>
                    <div className="text-slate-500">
                      不同貨主可有不同規則與費項組合
                    </div>
                  </div>
                </div>
                <p className="mt-6 text-lg leading-8 text-slate-700">
                  3PL
                  系統的價值不只在作業效率，還在於能否把完成了的服務準確累積成可對帳收入。AUTOCHAIN
                  讓作業與計費規則連在一起，減少月底再靠 Excel 整理。
                </p>
                <p className="mt-3 text-sm font-medium text-slate-500">
                  倉租可按固定 CBM 或最高 CBM 計算
                </p>
                <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3">
                  {pricingItems.map((item) => (
                    <div
                      key={item}
                      className="rounded-2xl bg-slate-50 px-5 py-5 text-center text-base font-semibold text-slate-800"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-[34px] bg-white p-8 shadow-xl shadow-slate-200/60">
                <div className="flex items-center gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-100 text-2xl text-teal-700">
                    <i className="ri-settings-4-line"></i>
                  </div>
                  <div>
                    <div className="text-xl font-semibold text-slate-900">
                      可逐步接上的擴展能力
                    </div>
                    <div className="text-slate-500">
                      先標準化流程，再擴展設備與系統整合
                    </div>
                  </div>
                </div>
                <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3">
                  {enablementItems.map((item) => (
                    <div
                      key={item}
                      className="rounded-2xl border border-slate-200 bg-white px-5 py-5 text-center text-sm font-semibold text-slate-800"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-[34px] bg-white p-8 shadow-xl shadow-slate-200/60">
              <span className="inline-flex rounded-full bg-sky-100 px-4 py-2 text-sm font-medium text-sky-700">
                TPLSP / 3PL Funding
              </span>
              <h2 className="mt-5 text-3xl font-bold text-slate-900">
                善用資助計劃，
                <span className="block text-slate-600">
                  讓 3PL 系統升級更容易落地
                </span>
              </h2>
              <p className="mt-4 text-lg leading-8 text-slate-600">
                若你是合資格第三方物流服務供應商，可考慮透過 TPLSP
                為系統升級、自動化設備與流程數碼化項目申請資助，減輕初期投資壓力。
              </p>

              <div className="mt-8 overflow-hidden rounded-[28px] border border-slate-200 bg-slate-50">
                <img
                  src="/tplsp_banner_2000x716.png"
                  alt="第三方物流資助計劃"
                  className="h-52 w-full object-cover"
                />
              </div>

              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-2xl bg-slate-50 p-5">
                  <div className="text-sm text-slate-500">最高資助</div>
                  <div className="mt-2 text-2xl font-bold text-slate-900">
                    HK$2,000,000
                  </div>
                </div>
                <div className="rounded-2xl bg-slate-50 p-5">
                  <div className="text-sm text-slate-500">資助比例</div>
                  <div className="mt-2 text-2xl font-bold text-slate-900">
                    最多三分之二
                  </div>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                  <i className="ri-check-line mt-1 text-sky-600"></i>
                  <p className="leading-7 text-slate-700">
                    適合第三方物流服務供應商申請系統與流程升級項目
                  </p>
                </div>
                <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                  <i className="ri-check-line mt-1 text-sky-600"></i>
                  <p className="leading-7 text-slate-700">
                    AUTOCHAIN 可配合 3PL WMS 方案規劃與資助方向整理
                  </p>
                </div>
              </div>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Button
                  variant="primary"
                  onClick={() => {
                    window.location.href = "/tplsp";
                  }}
                >
                  查看資助詳情
                </Button>
                <Button
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                  onClick={() =>
                    openWhatsApp(
                      "您好！我想了解第三方物流資助計劃與 AUTOCHAIN 3PL WMS 的配合方式。",
                      "Hello! I would like to learn how the 3PL funding scheme can work with AUTOCHAIN's 3PL WMS.",
                    )
                  }
                >
                  查詢資助配合
                </Button>
              </div>

              <div className="mt-8 grid grid-cols-1 gap-4 border-t border-slate-200 pt-6 sm:grid-cols-2">
                <div className="flex items-center justify-center rounded-lg bg-white p-3">
                  <img
                    src="/logo-TLB-tc.webp"
                    alt="貿易及物流局"
                    className="h-12"
                  />
                </div>
                <div className="flex items-center justify-center rounded-lg bg-white p-3">
                  <img
                    src="/logo-Gov-Funded.png"
                    alt="政府資助計劃"
                    className="h-12"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            準備好把 3PL 作業、計費與客戶管理整合到同一套系統？
          </h2>
          <p className="mt-6 text-xl leading-8 text-slate-300">
            由倉務現場到對帳報表，AUTOCHAIN
            協助第三方物流團隊建立更穩定、更可擴展的服務能力。
          </p>

          <div className="mt-8 rounded-[28px] border border-white/10 bg-white/5 p-8 backdrop-blur-sm">
            <div className="grid grid-cols-1 gap-4 text-slate-200 md:grid-cols-3">
              <div className="flex items-center justify-center">
                <i className="ri-search-line mr-2 text-xl"></i>
                <span>營運流程分析</span>
              </div>
              <div className="flex items-center justify-center">
                <i className="ri-calculator-line mr-2 text-xl"></i>
                <span>計費模式梳理</span>
              </div>
              <div className="flex items-center justify-center">
                <i className="ri-cpu-line mr-2 text-xl"></i>
                <span>系統與設備規劃</span>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:justify-center">
            <Button
              variant="primary"
              size="lg"
              className="px-8 py-4 text-lg"
              onClick={() => setShowDemo(true)}
            >
              預約方案演示
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-white bg-white/5 px-8 py-4 text-lg text-white hover:bg-white hover:text-slate-950"
              onClick={() =>
                openWhatsApp(
                  "您好！我想了解 AUTOCHAIN 第三方物流 WMS 是否適合我們目前的營運模式。",
                  "Hello! I would like to check whether AUTOCHAIN's 3PL WMS fits our current operation model.",
                )
              }
            >
              WhatsApp 查詢
            </Button>
          </div>

          <p className="mt-6 text-sm text-slate-400">
            24 小時內專人回覆 | 可按貨主結構、服務流程與計費模式提供建議
          </p>
        </div>
      </section>

      {showDemo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-8">
            <div className="mb-6 text-center">
              <h3 className="text-2xl font-bold text-slate-900">
                預約方案演示
              </h3>
              <p className="mt-2 text-slate-600">
                留下基本資料，我們會盡快與你聯絡。
              </p>
            </div>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="姓名"
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-transparent focus:ring-2 focus:ring-sky-500"
              />
              <input
                type="text"
                placeholder="公司名稱"
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-transparent focus:ring-2 focus:ring-sky-500"
              />
              <input
                type="email"
                placeholder="電子郵件"
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-transparent focus:ring-2 focus:ring-sky-500"
              />
              <input
                type="tel"
                placeholder="聯絡電話"
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-transparent focus:ring-2 focus:ring-sky-500"
              />
              <textarea
                placeholder="簡述目前貨主數量、倉務流程或系統需求"
                rows={3}
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-transparent focus:ring-2 focus:ring-sky-500"
              ></textarea>
              <div className="flex gap-3 pt-4">
                <Button variant="primary" className="flex-1">
                  提交查詢
                </Button>
                <Button variant="outline" onClick={() => setShowDemo(false)}>
                  取消
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
